// CRM: eine Aufgabe löschen — an einer Stelle, weil sie an vier Stellen angeboten wird
// (TasksPanel, Task-Queue, Work Session, Verlauf).
//
// Das Löschen selbst ist eine Zeile. Der Grund für diese Datei ist die GEGENRICHTUNG:
// `Activities.taskID` zeigt auf die Aufgabe zurück (helpers/activities.js), und der
// Verlauf baut daraus den Link „Zur Aufgabe". Bleibt der Zeiger stehen, führt er ins
// Leere. Gelöst wird er über `Tasks.activityIDs` — ein Aufruf je Eintrag, kein Scan.
// Die Liste ist die Regel mit einem Eintrag und die Ausnahme mit mehreren: derselbe
// Auftrag kann mehrere Anrufe gekostet haben, und jeder davon hängt an der Aufgabe.
//
// In `activityIDs` stehen `Activities`-IDs ODER `Emails`-IDs, und der Aufgabentyp sagt
// es NICHT verlässlich: `linkTaskActivity` hängt eine gesendete Mail an jede Aufgabe an,
// auch an eine vom Typ `todo` (EmailComposeModal), und ein Anruf landet genauso an einer
// Mail-Aufgabe. Der Typ ist deshalb nur die erste Vermutung; scheitert sie, wird die
// andere Tabelle versucht. Auf der falschen Tabelle ist es ein Update auf eine ID, die
// es dort nicht gibt — der Resolver bricht mit `attribute_exists` ab, es entsteht keine
// Geisterzeile.
//
// Die ANHÄNGE gehen mit: am Datensatz steht nur die schlanke Kopie, die Datei selbst ist
// eine `Documents`-Zeile plus S3-Objekt und bliebe sonst liegen — samt Recht, sie über
// ihre docId weiter herunterzuladen.
//
// `originActivityID` wird NICHT gelöst: dort zeigt die Aufgabe auf ihren Anlass, nicht
// der Anlass auf die Aufgabe — mit der Aufgabe verschwindet auch der Zeiger.
import { client } from "./amplifyClient";
import { deleteDocument } from "./api/document";
import {
  clearActivityTaskLink,
  clearEmailTaskLink,
  deleteTasks,
} from "../components/graphql/taskForm.queries";
import {
  DEMAND_TASK_TYPE,
  COMPANY_PAUSE_DAYS,
  DEMAND_DELETE_PAUSE_DAYS,
  pauseDemandRemindersForCompany,
} from "./demandInputs";

// `options.pauseCompany` kommt aus dem Loeschdialog: der Nutzer hat angehakt, dass
// bei diesem Kunden gerade nichts ansteht.
export const deleteTask = async (task, options = {}) => {
  // Eine geloeschte Bedarfsaufgabe ist die Antwort "nicht jetzt". Der Nachtlauf
  // sieht geloeschte Aufgaben nicht und legte sie am naechsten Morgen neu an — also
  // immer eine kurze Kundenpause, dieselbe Ruhezeit wie nach dem Erledigen. Auf
  // allen Loeschwegen, auch beim Mehrfachloeschen in der Aufgabenliste. Den Monat
  // gibt es nur, wenn der Nutzer ihn im Dialog anhakt.
  // Vor dem Loeschen, aber nicht blockierend: eine nicht gespeicherte Pause ist
  // laestig, eine Aufgabe, die sich nicht loeschen laesst, ist schlimmer.
  if (task?.objectType === DEMAND_TASK_TYPE && task?.companyID) {
    try {
      await pauseDemandRemindersForCompany(
        task.companyID,
        options.pauseCompany ? COMPANY_PAUSE_DAYS : DEMAND_DELETE_PAUSE_DAYS
      );
    } catch (error) {
      console.error("Kundenpause konnte nicht gesetzt werden", task.companyID, error);
    }
  }

  const byType =
    task?.taskType === "email"
      ? [clearEmailTaskLink, clearActivityTaskLink]
      : [clearActivityTaskLink, clearEmailTaskLink];

  for (const activityId of task?.activityIDs || []) {
    let lastError = null;
    let cleared = false;
    for (const query of byType) {
      try {
        await client.graphql({
          query,
          // "" und nicht null — beide Modelle tragen Field-Level-@auth, siehe Mutation.
          variables: { input: { id: activityId, taskID: "" } },
          skipGlobalError: true,
        });
        cleared = true;
        break;
      } catch (error) {
        lastError = error;
      }
    }
    // Ein hängender Rückzeiger ist ein toter Link, kein Datenverlust. Er darf das
    // Löschen nicht verhindern — sonst bleibt die Aufgabe stehen, obwohl der
    // Nutzer sie weghaben will. Gemeldet wird erst, wenn BEIDE Tabellen abgelehnt
    // haben; der erste Fehlschlag ist der normale Fall der falschen Vermutung.
    if (!cleared) {
      console.error("Rückzeiger konnte nicht gelöst werden", activityId, lastError);
    }
  }

  // Vor dem Löschen der Aufgabe: danach ist die Liste der Anhänge weg.
  // Ein fehlgeschlagenes Löschen darf die Aufgabe nicht stehen lassen — eine Datei,
  // die in S3 liegen bleibt, ist die harmlosere Hälfte (wie in activityDocuments.js).
  for (const doc of task?.documents || []) {
    if (!doc?.id) continue;
    try {
      await deleteDocument(doc.id);
    } catch (error) {
      console.error("Anhang konnte nicht gelöscht werden", doc.id, error);
    }
  }

  await client.graphql({
    query: deleteTasks,
    variables: { input: { id: task.id } },
  });
};

// Der Index `byAssignedUserIdByStatus` hat den ZUSAMMENGESETZTEN Sort Key
// `status#dueDate`. Amplifys Resolver bricht jedes Update ab, das nur einen der
// beiden Teile mitschickt: „you must provide all fields for the key". Erledigen
// (status) und Verschieben (dueDate) sind aber genau solche Updates.
//
// Beide muessen ausserdem echte Werte tragen — der Resolver klebt sie zu
// `"${status}#${dueDate}"` zusammen, ein `null` landet dort als das Wort "null".
// Deshalb "" als Rueckfallwert fuer eine Aufgabe ohne Faelligkeit.
export const withTaskSortKey = (task, changes) => {
  if (!("status" in changes) && !("dueDate" in changes)) return changes;
  return {
    ...changes,
    status: changes.status ?? task?.status ?? "open",
    dueDate: changes.dueDate ?? task?.dueDate ?? "",
    // Verschieben heisst: nochmal erinnern. Der Versandstempel haengt an der ALTEN
    // Faelligkeit, also hier zuruecknehmen — sonst bleibt jede verschobene Aufgabe
    // stumm, egal wo verschoben wurde (Queue, Work Session, Formular).
    ...("dueDate" in changes ? { reminderSentAt: null } : {}),
  };
};

// Die verknuepften Objekte EINES Typs. `associatedObjectIds` ist die Mehrfachauswahl,
// `objectID` dieselbe Verknuepfung in der Einzahl (aeltere Aufgaben und der
// HubSpot-Import tragen nur die). Den Typ je ID kennt nur `associatedObjects`; eine ID
// ohne Eintrag dort ist eine Kundenanfrage — andere Typen gibt es in der Liste erst,
// seit Auftraege mit ausgewaehlt werden koennen.
export const taskObjectIds = (task, type) => {
  const all = Array.isArray(task?.associatedObjectIds)
    ? task.associatedObjectIds.filter(Boolean)
    : [];
  if (all.length > 0) {
    let types = new Map();
    try {
      const raw = task.associatedObjects;
      const list = typeof raw === "string" ? JSON.parse(raw) : raw;
      if (Array.isArray(list)) types = new Map(list.map((o) => [o?.id, o?.type]));
    } catch (error) {
      // Kaputtes JSON: wie eine Aufgabe ohne Typen behandeln.
    }
    return all.filter((id) => (types.get(id) || "CustomerRequest") === type);
  }
  return task?.objectID && (task.objectType || "CustomerRequest") === type
    ? [task.objectID]
    : [];
};

// Die verknuepften Kundenanfragen einer Aufgabe.
export const taskRequestIds = (task) => taskObjectIds(task, "CustomerRequest");

// Die drei Aufgaben-Status. `status` ist der erste Teil des zusammengesetzten Sort Keys
// `status#dueDate` — ein neuer Wert braucht deshalb kein Schema und keinen Push, aber er
// darf kein Praefix eines anderen sein (der Filter fragt mit beginsWith).
// ponytail: nur die Werte + englische Beschriftung; uebersetzt wird an der Anzeigestelle
// mit t(), alle drei Labels stehen schon in common.json.
export const TASK_STATUSES = [
  { value: "open", label: "Open" },
  { value: "inProgress", label: "In progress" },
  { value: "completed", label: "Completed" },
];

// ─────────────────────────────────────────────────────────────────────────────
// Firma und Kundenkontakt einer Aufgabe auflösen.
//
// `Tasks` trägt bewusst KEINE @hasOne-Verbindungen mehr (Schema: die API ist am
// Ressourcenlimit von CloudFormation, jede Verbindung kostet eigene Resolver). Die
// Felder `company` und `contactPerson` gibt es serverseitig nicht mehr — sie werden
// hier nach dem Laden nachgetragen, in derselben Form wie vorher, damit die Aufrufer
// (Work Session, Queue, TasksPanel, Verlauf) unverändert weiterlesen können.
//
// Ein Alias je ID in EINER Abfrage — dasselbe Muster wie `emailsByIdsDocument` im
// Verlauf. Dazu ein Cache auf Modulebene: eine Aufgabenliste zeigt dieselbe Handvoll
// Firmen und Kontakte immer wieder.
//
// ponytail: der Cache läuft nie ab. Ceiling: ändert jemand in einer anderen
// Registerkarte den Firmennamen, steht hier bis zum Neuladen der alte. Wenn das
// stört, gehört ein `invalidateTaskRelations()` an die Speicher-Pfade — nicht
// vorher.
const relationCache = { companies: new Map(), contacts: new Map() };

const COMPANY_FIELDS = `
      id
      name
      type
      phone
      websiteUrl
      doNotContact`;

// Die drei Anrede-Felder müssen mit: ohne sie sind die Platzhalter im Compose-Fenster
// aus einer Aufgabe leer, obwohl der Kontakt sie trägt (C-II).
const CONTACT_FIELDS = `
      id
      firstName
      lastName
      email
      phone
      mobil
      salutation
      gender
      personalSalutation`;

const BATCH = 50;

export const fetchByIds = async (ids, cache, getter, fields) => {
  const missing = [...new Set(ids.filter(Boolean))].filter((id) => !cache.has(id));
  for (let start = 0; start < missing.length; start += BATCH) {
    const chunk = missing.slice(start, start + BATCH);
    const query = `query Batch {
${chunk
  .map(
    (id, index) => `  r${index}: ${getter}(id: ${JSON.stringify(id)}) {${fields}
  }`,
  )
  .join("\n")}
}`;
    try {
      const data = (await client.graphql({ query, skipGlobalError: true })).data || {};
      chunk.forEach((id, index) => cache.set(id, data[`r${index}`] || null));
    } catch (error) {
      // Nicht lesbar ist kein Anwendungsfehler — die Aufgabe bleibt sichtbar, nur
      // ohne Firmenname bzw. Kontakt. Als null merken, damit nicht erneut gefragt wird.
      console.error("Verknuepfung der Aufgabe nicht lesbar", error);
      chunk.forEach((id) => cache.set(id, null));
    }
  }
};

/**
 * Trägt `company` und `contactPerson` an einer Liste von Aufgaben nach.
 * Gibt eine neue Liste zurück; die Eingabe bleibt unangetastet.
 */
export const hydrateTasks = async (tasks) => {
  const rows = tasks || [];
  if (rows.length === 0) return rows;

  await Promise.all([
    fetchByIds(
      rows.map((task) => task?.companyID),
      relationCache.companies,
      "getCompanies",
      COMPANY_FIELDS,
    ),
    fetchByIds(
      rows.map((task) => task?.contactPersonId),
      relationCache.contacts,
      "getCompaniesContactPersons",
      CONTACT_FIELDS,
    ),
  ]);

  return rows.map((task) =>
    task
      ? {
          ...task,
          company: relationCache.companies.get(task.companyID) || null,
          contactPerson: relationCache.contacts.get(task.contactPersonId) || null,
        }
      : task,
  );
};

/** Eine einzelne Aufgabe — derselbe Weg, nur bequemer. */
export const hydrateTask = async (task) =>
  task ? (await hydrateTasks([task]))[0] : task;
